import { useMemo, useState } from 'react';
import { Plus, RefreshCw, ShieldCheck, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { PageContainer, PageHeader, StatsGrid, StatCard, FilterBar, ItemCard, ItemCardIcon, ItemActions, EmptyState, LoadingState, ErrorState, DeleteConfirmModal, TypeBadge } from '../../components/common';
import { TlsContextEditor } from '../../components/foundation/TlsContextEditor';
import { useViewMode } from '../../hooks/useViewMode';
import { useDeleteTlsContext, useRunTlsContextBackfill, useTlsContextInventory, useTlsContexts, type TlsContextBackfillResponse } from '../../hooks/useTlsContexts';
import type { TlsContextInfo } from '@sbclite/core';

type FilterType = 'all' | 'enabled' | 'disabled';

const FILTER_OPTIONS = [
  { value: 'all', label: 'All contexts' },
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
];

function InventoryPanel() {
  const inventoryQuery = useTlsContextInventory();
  const backfillMutation = useRunTlsContextBackfill();
  const [confirmApply, setConfirmApply] = useState(false);
  const [lastResult, setLastResult] = useState<TlsContextBackfillResponse | null>(null);
  const [backfillError, setBackfillError] = useState<string | null>(null);

  const summary = inventoryQuery.data?.summary;
  const blockers = summary?.blockers.flatMap((entry) => entry.blockers) ?? [];

  return (
    <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Inventory & backfill</h3>
          <p className="text-sm text-gray-600">Dry-run or apply legacy TLS policy backfill and inspect cleanup blockers.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className="rounded-md border border-gray-300 px-3 py-2 text-sm" onClick={() => inventoryQuery.refetch()} disabled={inventoryQuery.isFetching}>
            <RefreshCw className={`mr-2 inline h-4 w-4 ${inventoryQuery.isFetching ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button type="button" className="rounded-md border border-blue-300 px-3 py-2 text-sm text-blue-700" onClick={async () => {
            try {
              setBackfillError(null);
              setLastResult(await backfillMutation.mutateAsync(false));
            } catch (error) {
              setBackfillError(error instanceof Error ? error.message : 'Dry-run failed');
            }
          }} disabled={backfillMutation.isPending}>
            Dry-run
          </button>
          <button type="button" className="rounded-md bg-blue-600 px-3 py-2 text-sm text-white disabled:opacity-50" disabled={!confirmApply || backfillMutation.isPending} onClick={async () => {
            try {
              setBackfillError(null);
              setLastResult(await backfillMutation.mutateAsync(true));
            } catch (error) {
              setBackfillError(error instanceof Error ? error.message : 'Backfill failed');
            }
          }}>
            Apply backfill
          </button>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={confirmApply} onChange={(event) => setConfirmApply(event.target.checked)} />
        I understand backfill will attach only representable legacy policy and will remain blocked when references are missing.
      </label>

      {summary ? (
        <div className="grid gap-3 md:grid-cols-4">
          <div className="rounded-md bg-gray-50 p-3"><div className="text-xs uppercase text-gray-500">Ready for cleanup</div><div className={`text-lg font-semibold ${summary.readyForCleanup ? 'text-green-700' : 'text-amber-700'}`}>{summary.readyForCleanup ? 'Yes' : 'No'}</div></div>
          <div className="rounded-md bg-gray-50 p-3"><div className="text-xs uppercase text-gray-500">Backfillable</div><div className="text-lg font-semibold text-blue-700">{summary.backfillable}</div></div>
          <div className="rounded-md bg-gray-50 p-3"><div className="text-xs uppercase text-gray-500">Blocked</div><div className="text-lg font-semibold text-red-700">{summary.blocked}</div></div>
          <div className="rounded-md bg-gray-50 p-3"><div className="text-xs uppercase text-gray-500">Enabled</div><div className="text-lg font-semibold text-gray-900">{summary.totals.enabled}</div></div>
        </div>
      ) : null}

      {blockers.length > 0 ? (
        <div className="space-y-2 rounded-md border border-red-200 bg-red-50 p-3">
          <div className="font-medium text-red-900 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Cleanup blockers</div>
          <ul className="space-y-1 text-sm text-red-900">
            {blockers.map((blocker, index) => <li key={`${blocker.code}-${index}`}>{String(blocker.code)} — {String(blocker.message)}</li>)}
          </ul>
        </div>
      ) : null}

      {lastResult ? (
        <div className="space-y-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900">
          <div className="font-medium flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Last backfill result</div>
          <div>{lastResult.dryRun ? 'Dry-run' : 'Applied'} — {lastResult.appliedCount} context(s) processed. Cleanup ready: {lastResult.cleanupReady ? 'yes' : 'no'}.</div>
        </div>
      ) : null}

      {backfillError ? <div className="text-sm text-red-700">{backfillError}</div> : null}

      {inventoryQuery.error ? <div className="text-sm text-red-700">Failed to load inventory.</div> : null}
    </div>
  );
}

export default function TlsContextsPage() {
  const [viewMode, setViewMode] = useViewMode();
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [editingTlsContext, setEditingTlsContext] = useState<TlsContextInfo | null>(null);
  const [tlsContextToDelete, setTlsContextToDelete] = useState<TlsContextInfo | null>(null);

  const tlsContextsQuery = useTlsContexts();
  const deleteMutation = useDeleteTlsContext();
  const tlsContexts = tlsContextsQuery.data ?? [];

  const filteredContexts = useMemo(() => tlsContexts.filter((context) => {
    const matchesFilter = filterType === 'all' ? true : filterType === 'enabled' ? context.enabled : !context.enabled;
    const matchesSearch = searchQuery === '' || context.name.toLowerCase().includes(searchQuery.toLowerCase()) || (context.description?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);
    return matchesFilter && matchesSearch;
  }), [tlsContexts, filterType, searchQuery]);

  const stats = {
    total: tlsContexts.length,
    enabled: tlsContexts.filter((context) => context.enabled).length,
    disabled: tlsContexts.filter((context) => !context.enabled).length,
  };

  if (tlsContextsQuery.isLoading) return <LoadingState />;
  if (tlsContextsQuery.error) return <ErrorState title="Failed to load TLS contexts" message={tlsContextsQuery.error instanceof Error ? tlsContextsQuery.error.message : 'Unknown error'} onRetry={() => tlsContextsQuery.refetch()} />;

  return (
    <PageContainer>
      <PageHeader
        title="TLS Contexts"
        description="Reusable TLS policy objects for SIP interfaces and peers"
        onRefresh={() => tlsContextsQuery.refetch()}
        isRefreshing={tlsContextsQuery.isFetching}
        primaryAction={{ label: 'Create TLS Context', icon: Plus, onClick: () => { setEditingTlsContext(null); setShowEditor(true); } }}
      />

      <StatsGrid>
        <StatCard label="Total" value={stats.total} icon={ShieldCheck} iconBgColor="bg-blue-100" iconColor="text-blue-600" />
        <StatCard label="Enabled" value={stats.enabled} icon={CheckCircle2} iconBgColor="bg-green-100" iconColor="text-green-600" valueColor="text-green-600" />
        <StatCard label="Disabled" value={stats.disabled} icon={AlertTriangle} iconBgColor="bg-amber-100" iconColor="text-amber-600" valueColor="text-amber-600" />
      </StatsGrid>

      <FilterBar searchPlaceholder="Search TLS contexts..." searchValue={searchQuery} onSearchChange={setSearchQuery} filterOptions={FILTER_OPTIONS} filterValue={filterType} onFilterChange={(value) => setFilterType(value as FilterType)} viewMode={viewMode} onViewModeChange={setViewMode} />

      {filteredContexts.length === 0 ? (
        <EmptyState icon={ShieldCheck} title="No TLS contexts found" description="Create a reusable TLS policy and attach it to SIP interfaces or peers." isFiltered={searchQuery !== '' || filterType !== 'all'} action={{ label: 'Create TLS Context', icon: Plus, onClick: () => setShowEditor(true) }} />
      ) : viewMode === 'grid' ? (
        <div className="grid gap-6 lg:grid-cols-2">
          {filteredContexts.map((context) => (
            <ItemCard
              key={context.id}
              icon={<ItemCardIcon icon={<ShieldCheck className={`h-5 w-5 ${context.enabled ? 'text-blue-600' : 'text-gray-400'}`} />} bgColor={context.enabled ? 'bg-blue-100' : 'bg-gray-100'} />}
              title={context.name}
              subtitle={context.description || 'TLS policy'}
              status={<TypeBadge label={context.enabled ? 'Enabled' : 'Disabled'} variant={context.enabled ? 'green' : 'gray'} size="sm" />}
              details={[
                { label: 'Min version', value: context.minVersion },
                { label: 'Peer verify', value: context.verifyPeer ? 'On' : 'Off' },
                { label: 'Hostname verify', value: context.verifyHostname ? 'On' : 'Off' },
              ]}
              tags={<div className="flex flex-wrap gap-1">{[context.certificateId ? 'cert' : null, context.caCertificateId ? 'ca' : null, context.clientCertificateRequired ? 'client-cert' : null].filter((tag): tag is string => tag !== null).map((tag) => <TypeBadge key={tag} label={tag} variant="gray" size="sm" />)}</div>}
              actions={<ItemActions variant="card" actions={[{ kind: 'edit', onClick: () => { setEditingTlsContext(context); setShowEditor(true); } }, { kind: 'delete', onClick: () => { setTlsContextToDelete(context); } }]} />}
            />
          ))}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Context</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Policy</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filteredContexts.map((context) => (
                <tr key={context.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{context.name}</div>
                    <div className="text-sm text-gray-500">{context.description || '—'}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    <div>Min version: {context.minVersion}</div>
                    <div>Verify peer: {context.verifyPeer ? 'on' : 'off'}</div>
                    <div>Hostname: {context.verifyHostname ? 'on' : 'off'}</div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <ItemActions variant="table" actions={[{ kind: 'edit', onClick: () => { setEditingTlsContext(context); setShowEditor(true); } }, { kind: 'delete', onClick: () => { setTlsContextToDelete(context); } }]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <InventoryPanel />

      <TlsContextEditor
        isOpen={showEditor}
        onClose={() => { setShowEditor(false); setEditingTlsContext(null); }}
        onSuccess={() => tlsContextsQuery.refetch()}
        editingTlsContext={editingTlsContext}
      />

      {tlsContextToDelete ? (
        <DeleteConfirmModal
          isOpen={true}
          onClose={() => setTlsContextToDelete(null)}
          onConfirm={async () => {
            await deleteMutation.mutateAsync(tlsContextToDelete.id);
            setTlsContextToDelete(null);
            await tlsContextsQuery.refetch();
          }}
          title="Delete TLS Context"
          entityType="tls context"
          entityName={tlsContextToDelete.name}
          isDeleting={deleteMutation.isPending}
          warningMessage="Deleting a TLS context can break attached SIP interfaces and peers."
        />
      ) : null}
    </PageContainer>
  );
}
